/**
 * Iris custom answers — operator-authored Q→A pairs. Each entry pairs a
 * question and a set of trigger phrases with an exact answer Iris returns
 * verbatim (in the customer's language) when an inbound message matches,
 * instead of generating its own reply. A matching custom answer is the
 * highest-priority source: it wins over the AI's own reply. Proxied through
 * operations at `/api/messaging/iris/custom-answers` (operations is the FE's
 * auth boundary + RBAC gate; it forwards to ai-operations with the shared
 * service key). Powers Settings → Iris → Train.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/custom-answers';

/** Max length of a custom-answer question (matches the backend validator). */
export const CUSTOM_ANSWER_QUESTION_MAX_LENGTH = 200;
/** Max length of a custom-answer answer (matches the backend validator). */
export const CUSTOM_ANSWER_MAX_LENGTH = 2000;
/** Max number of trigger phrases per custom answer (matches the backend). */
export const CUSTOM_ANSWER_MAX_TRIGGERS = 25;

export interface CustomAnswer {
  id: string;
  question: string;
  triggers: string[];
  answer: string;
  enabled: boolean;
  sortOrder: number;
}

export interface CreateCustomAnswerPayload {
  question: string;
  answer: string;
  triggers?: string[];
  enabled?: boolean;
}

export interface UpdateCustomAnswerPayload {
  question?: string;
  answer?: string;
  triggers?: string[];
  enabled?: boolean;
}

export const irisCustomAnswersKey = ['messaging', 'iris-custom-answers'] as const;

export function useCustomAnswers() {
  return useQuery({
    queryKey: irisCustomAnswersKey,
    queryFn: () => apiGet<CustomAnswer[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateCustomAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCustomAnswerPayload) =>
      apiPost<CustomAnswer, CreateCustomAnswerPayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisCustomAnswersKey });
    },
  });
}

export function useUpdateCustomAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateCustomAnswerPayload) =>
      apiPut<CustomAnswer, UpdateCustomAnswerPayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisCustomAnswersKey });
    },
  });
}

export function useDeleteCustomAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisCustomAnswersKey });
    },
  });
}
